import * as ServerTypes from "@/models/types";
import { useUserSlice } from "@/models/useUserSlice";
import { Router } from "@/_router";
import { getBasePath } from "@/models/utils";
import { decipher } from "../../../server/cloudflare/src/lib/encryption";

export const __DEV__ =
  Boolean(process) && process.env.NODE_ENV === "development";

export const getServerUrl = () => {
  let url = "https://api.purelynovels.com";

  if (__DEV__) {
    url = "http://localhost:8787";
  }

  // url = "https://purelynovels-api-test.dkt201474-com.workers.dev";

  return url;
};

export const parseJwt = (token: string): ServerTypes.AuthUser => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const authenticateAsync = async (
  params: ServerTypes.ApiRequestAuthenticate["payload"]
): Promise<ServerTypes.ApiResponseAuthenticate> => {
  try {
    const requestPayload: ServerTypes.ApiRequest = {
      action: "Authenticate",
      payload: params,
    };

    const serverUrl = getServerUrl();

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => useAuthorized(res));
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

const useAuthorized = (res: Response): any => {
  if (res.status === 403) {
    Router.replace(`${getBasePath()}/auth?status=logout`);

    return res.json();
  }

  return res.json();
};

export const getTokenAsync = (): string => {
  const token = useUserSlice.getState().userToken;

  // 1. Get user token if present and valid
  if (token) {
    const authUser = parseJwt(token);
    const isExpired = authUser?.exp && Date.now() >= authUser.exp * 1000;

    if (!isExpired) return token;

    // Token expired → clear and fall through
    useUserSlice.getState().setUserToken(undefined);
  }

  // 2. No usable token → redirect home
  Router.replace(`${getBasePath()}/`);
  return "";
};

export const userAsync = async (
  params: ServerTypes.ApiRequestUser["payload"]
): Promise<ServerTypes.ApiResponseUser> => {
  try {
    const token = getTokenAsync();
    if (!token) return;

    const requestPayload: ServerTypes.ApiRequest = {
      action: "User",
      payload: params,
    };

    const serverUrl = getServerUrl();

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: getTokenAsync(),
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const subscriptionAsync = async (
  params: ServerTypes.ApiRequestSubscription["payload"]
): Promise<ServerTypes.ApiResponseSubscription> => {
  try {
    const requestPayload: ServerTypes.ApiRequest = {
      action: "Subscription",
      payload: params,
    };

    const serverUrl = getServerUrl();

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: getTokenAsync(),
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => useAuthorized(res));
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const bookAsync = async (
  params: ServerTypes.ApiRequestBook["payload"]
): Promise<ServerTypes.ApiResponseBook> => {
  try {
    const requestPayload: ServerTypes.ApiRequest = {
      action: "Book",
      payload: params,
    };

    const serverUrl = getServerUrl();

    const res = await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: getTokenAsync(),
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((r) => useAuthorized(r));

    if (res?.data?.aiChapter) {
      res.data.aiChapter = await decipher(res.data.aiChapter);
    }

    return res;
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const preferenceAsync = async (
  params: ServerTypes.ApiRequestPreference["payload"]
): Promise<ServerTypes.ApiResponsePreference> => {
  try {
    const token = getTokenAsync();
    if (!token) return;

    const requestPayload: ServerTypes.ApiRequest = {
      action: "Preference",
      payload: params,
    };

    const serverUrl = getServerUrl();

    return await fetch(serverUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: getTokenAsync(),
      },
      body: JSON.stringify({ event: requestPayload }),
    }).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
  }
};

export const remoteConfigAsync = async (params?: {
  queryParams?: { platform?: string; version?: string };
}): Promise<ServerTypes.RemoteConfig> => {
  try {
    const serverUrl = getServerUrl();

    const url = new URL(`${serverUrl}/remote-config`);

    if (params?.queryParams) {
      Object.entries(params.queryParams).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    return await fetch(url).then((res) => res.json());
  } catch (e) {
    __DEV__ && console.log(e);
  }
};
